import { useAssignmentTask } from '@components/assignments/task/AssignmentTaskContext';
import { VIDEO_HEARTBEAT_INTERVAL_MS } from '@consts';
import { useBlockOutputsCollector } from '@hooks/useBlockOutputsCollector';
import { AssignmentBlockOutputActionType } from '@models/assignments';
import { useCallback, useEffect, useRef } from 'react';

export enum VideoEventName {
  PLAY = 'play',
  PAUSE = 'pause',
  SEEKED = 'seeked',
  TIMEUPDATE = 'timeupdate',
  VOLUME_CHANGE = 'volumechange',
  QUALITY_CHANGE = 'qualitychange',
  FULLSCREEN_CHANGE = 'fullscreenchange',
  FINISHED = 'finished'
}

export interface VideoEventData {
  seconds?: number;
  duration?: number;
  percent?: number;
  volume?: number;
  muted?: boolean;
  quality?: string;
  fullscreen?: boolean;
}

const VOLUME_DEBOUNCE_MS = 500;

export function useVideoEventLogger(block: { entityId: string; versionId?: string }) {
  const { state, isEditorMode } = useAssignmentTask();
  const { logBlockOutput } = useBlockOutputsCollector();
  const lastHeartbeatRef = useRef<number>(0);
  const volumeDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fullscreenDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (volumeDebounceTimerRef.current) {
        clearTimeout(volumeDebounceTimerRef.current);
      }

      if (fullscreenDebounceTimerRef.current) {
        clearTimeout(fullscreenDebounceTimerRef.current);
      }
    };
  }, []);

  const EVENT_MAP: Record<VideoEventName, AssignmentBlockOutputActionType> = {
    [VideoEventName.PLAY]: AssignmentBlockOutputActionType.VIDEO_PLAYED,
    [VideoEventName.PAUSE]: AssignmentBlockOutputActionType.VIDEO_PAUSED,
    [VideoEventName.SEEKED]: AssignmentBlockOutputActionType.VIDEO_REWINDED,
    [VideoEventName.TIMEUPDATE]: AssignmentBlockOutputActionType.VIDEO_STREAMED,
    [VideoEventName.VOLUME_CHANGE]: AssignmentBlockOutputActionType.VIDEO_VOLUME_CHANGED,
    [VideoEventName.QUALITY_CHANGE]: AssignmentBlockOutputActionType.VIDEO_QUALITY_CHANGED,
    [VideoEventName.FULLSCREEN_CHANGE]: AssignmentBlockOutputActionType.VIDEO_FULLSCREEN_CHANGED,
    [VideoEventName.FINISHED]: AssignmentBlockOutputActionType.VIDEO_FINISHED
  };

  const log = useCallback(
    (event: VideoEventName, data: VideoEventData) => {
      const { nodeEntityId, goalSubscriptionId } = state.assignment;
      logBlockOutput(nodeEntityId, goalSubscriptionId, {
        blockEntityId: block.entityId,
        blockVersionId: block.versionId,
        actionType: EVENT_MAP[event],
        value: data,
        timestamp: new Date().toISOString()
      });
    },
    [state.assignment, block.entityId, block.versionId, logBlockOutput]
  );

  return useCallback(
    (event: VideoEventName, data: VideoEventData) => {
      if (isEditorMode) return;

      if (event === VideoEventName.TIMEUPDATE) {
        if (typeof data.seconds !== 'number') return;
        if (Math.abs(data.seconds - lastHeartbeatRef.current) < VIDEO_HEARTBEAT_INTERVAL_MS) return;
        lastHeartbeatRef.current = data.seconds;
      }

      if (event === VideoEventName.VOLUME_CHANGE) {
        if (volumeDebounceTimerRef.current) {
          clearTimeout(volumeDebounceTimerRef.current);
        }
        volumeDebounceTimerRef.current = setTimeout(() => {
          log(event, data);
          volumeDebounceTimerRef.current = null;
        }, VOLUME_DEBOUNCE_MS);
        return;
      }

      if (event === VideoEventName.FULLSCREEN_CHANGE) {
        if (fullscreenDebounceTimerRef.current) {
          clearTimeout(fullscreenDebounceTimerRef.current);
        }
        fullscreenDebounceTimerRef.current = setTimeout(() => {
          log(event, data);
          fullscreenDebounceTimerRef.current = null;
        }, 300);
        return;
      }

      log(event, data);
    },
    [isEditorMode, log]
  );
}
